/* eslint-disable no-console */
/* This module IS the dev console trace, so the console rule is switched off for
 * the file. */

import type { ChatId } from 'src/content/ChatV2/types/ids';
import type { InspectionEvent } from 'xstate';

import { CHAT_STREAM_EVENT } from './actions';

const STORAGE_KEY = 'egpt.chatV2.inspect';

const HIGH_FREQUENCY_EVENTS = new Set<string>([CHAT_STREAM_EVENT.STREAM_CHUNK]);

export type InspectorMode = 'on' | 'off' | 'verbose';

function isBrowserDev(): boolean {
  return (
    Boolean(import.meta.env?.DEV) &&
    typeof window !== 'undefined' &&
    typeof window.document !== 'undefined'
  );
}

function readStoredMode(): InspectorMode {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'off' || raw === 'verbose' ? raw : 'on';
  } catch {
    return 'on';
  }
}

let mode: InspectorMode | undefined;

function currentMode(): InspectorMode {
  mode ??= readStoredMode();
  return mode;
}

/** Change the mode for running actors AND persist it for the next reload. */
export function setInspectorMode(next: InspectorMode): void {
  mode = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {}
}

/** True when transitions are being logged — dev browser, not switched off. */
export function isInspectorEnabled(): boolean {
  return isBrowserDev() && currentMode() !== 'off';
}

function stateValueOf(snapshot: unknown): string | undefined {
  const value = (snapshot as { value?: unknown } | undefined)?.value;
  if (value === undefined) {
    return undefined;
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/**
 * The `inspect` handler for ONE chat's actor, or `undefined` outside a local dev
 * browser.
 */
export function createMachineInspector(
  chatId: ChatId
): ((event: InspectionEvent) => void) | undefined {
  if (!isBrowserDev()) {
    return undefined;
  }

  const label = `[chatV2:${String(chatId)}]`;
  let lastState: string | undefined;
  let highFrequencyCount = 0;

  return (inspectionEvent: InspectionEvent) => {
    if (currentMode() === 'off') {
      return;
    }
    const verbose = currentMode() === 'verbose';

    if (inspectionEvent.type === '@xstate.event') {
      if (verbose) {
        console.debug(
          `${label} event ${inspectionEvent.event?.type}`,
          inspectionEvent.event
        );
      }
      return;
    }

    if (inspectionEvent.type === '@xstate.snapshot') {
      const value = stateValueOf(inspectionEvent.snapshot);
      if (value === undefined) {
        return;
      }

      const cause = inspectionEvent.event?.type;

      if (value === lastState) {
        if (cause && HIGH_FREQUENCY_EVENTS.has(cause)) {
          highFrequencyCount += 1;
        }
        return;
      }

      const from = lastState ?? '(spawn)';
      const via = cause ? ` via ${cause}` : '';
      const tally =
        highFrequencyCount > 0
          ? ` [${highFrequencyCount} chunk${
              highFrequencyCount === 1 ? '' : 's'
            }]`
          : '';

      console.debug(`${label} ${from} → ${value}${via}${tally}`);

      lastState = value;
      highFrequencyCount = 0;
    }
  };
}

if (isBrowserDev()) {
  (window as unknown as Record<string, unknown>).__chatV2Inspect = {
    on: () => setInspectorMode('on'),
    off: () => setInspectorMode('off'),
    verbose: () => setInspectorMode('verbose'),
    mode: () => currentMode()
  };
}
